/**
 * @module features/client/pages/ClientLoginPage
 * @description Ultra-clean, minimalist Client Portal sign-in interface modeled directly after the Folk design reference.
 * Authenticates Client Organization accounts via `/api/v1/client/auth/login/` (with 2FA support),
 * dynamically applies the organization's `theme_color`, and routes to `/client/display-module`.
 */

import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sun,
  Moon,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  ShieldCheck,
} from "lucide-react";
import { APP_NAME } from "@/config/env";
import { applyClientThemeColor } from "@/features/clients";
import {
  clientLoginWithPassword,
  clientVerify2FACode,
  useAuth,
} from "@/features/auth";

export function ClientLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const [requires2FA, setRequires2FA] = React.useState(false);
  const [tempToken, setTempToken] = React.useState<string | null>(null);
  const [totpCode, setTotpCode] = React.useState("");

  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const themeFromParam = params.get("theme_color");
      const storedTheme = localStorage.getItem("client_theme_color");
      const activeColor = themeFromParam || storedTheme;
      if (activeColor && /^#[0-9a-fA-F]{6}$/.test(activeColor)) {
        applyClientThemeColor(activeColor);
      }
    } catch {
      // safe fallback
    }
  }, []);

  const [isDark, setIsDark] = React.useState(() => {
    return document.documentElement.classList.contains("dark");
  });

  const toggleTheme = React.useCallback(() => {
    const nextIsDark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", nextIsDark);
    localStorage.setItem("theme", nextIsDark ? "dark" : "light");
    setIsDark(nextIsDark);
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage("Please enter your email address.");
      return;
    }
    if (!password) {
      setErrorMessage("Please enter your password.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await clientLoginWithPassword({
        email: trimmedEmail,
        password,
      });

      if (result.requires_2fa && result.temp_token) {
        setTempToken(result.temp_token);
        setRequires2FA(true);
        return;
      }

      if (result.user) {
        if (result.user.client?.theme_color) {
          applyClientThemeColor(result.user.client.theme_color);
        }
        login(result.user);
        navigate("/client/display-module", { replace: true });
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : "Invalid email or password. Please try again.";
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempToken || !totpCode.trim()) {
      setErrorMessage("Please enter your 6-digit verification code.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const data = await clientVerify2FACode({
        temp_token: tempToken,
        code: totpCode.trim(),
      });
      if (data.user.client?.theme_color) {
        applyClientThemeColor(data.user.client.theme_color);
      }
      login(data.user);
      navigate("/client/display-module", { replace: true });
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : "Invalid verification code. Please try again.";
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleClick = () => {
    setErrorMessage(
      "Google Workspace single sign-on for client accounts is undergoing configuration."
    );
  };

  const brandDisplay = APP_NAME.toLowerCase();

  return (
    <main className="min-h-dvh w-full flex flex-col justify-between bg-background dark:bg-zinc-950 text-foreground selection:bg-zinc-200 dark:selection:bg-zinc-800 relative px-6 py-6 sm:px-12 sm:py-8">
      {/* Top Header: Brand Wordmark (top-left) & Theme Switcher (top-right) */}
      <header className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2 select-none">
          <div
            style={{ borderColor: "var(--color-brand-primary)" }}
            className="h-6 w-6 rounded-full border-[2.5px] border-foreground flex items-center justify-center"
          >
            <div
              style={{ backgroundColor: "var(--color-brand-primary)" }}
              className="h-2 w-2 rounded-full bg-foreground"
            />
          </div>
          <span className="text-xl sm:text-2xl font-bold tracking-tight text-foreground font-sans">
            {brandDisplay}.
          </span>
        </div>

        {/* Minimal theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? (
            <Sun className="h-4 w-4 text-amber-400" />
          ) : (
            <Moon className="h-4 w-4 text-zinc-600" />
          )}
        </button>
      </header>

      {/* Center: Split Authentication Form */}
      <div className="w-full flex flex-col items-center justify-center py-10 my-auto">
        <div className="w-full max-w-[340px] sm:max-w-[640px] md:max-w-[680px] flex flex-col">
          {/* Header Typography */}
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-[28px] font-bold tracking-tight text-foreground font-sans">
              Welcome to {brandDisplay}
            </h1>
            <p className="text-xs sm:text-[13px] text-muted-foreground mt-2 leading-relaxed">
              IoT device and Digital Display Module workspace for client organizations
            </p>
          </div>

          {/* Feedback banner */}
          {errorMessage && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2.5 animate-in fade-in duration-200"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="flex-1 text-[11px] leading-relaxed">
                {errorMessage}
              </div>
            </div>
          )}

          {requires2FA ? (
            <form
              onSubmit={handleVerify2FA}
              className="max-w-sm mx-auto w-full space-y-4 rounded-2xl border border-border/80 dark:border-zinc-800 bg-card dark:bg-zinc-900/60 p-6 shadow-xs"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                <span>Two-Factor Authentication</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the 6-digit authenticator code or recovery code for your
                account.
              </p>
              <div className="space-y-1.5 text-left">
                <label
                  htmlFor="client-2fa-input"
                  className="text-xs font-semibold text-foreground"
                >
                  Verification Code
                </label>
                <input
                  id="client-2fa-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  required
                  className="h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-xs sm:text-sm font-mono tracking-widest text-foreground focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRequires2FA(false);
                    setTempToken(null);
                    setTotpCode("");
                  }}
                  className="h-10 px-3 rounded-md border border-zinc-300 dark:border-zinc-700 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    backgroundColor: "var(--color-brand-primary)",
                    color: "var(--color-brand-foreground)",
                  }}
                  className="flex-1 h-10 rounded-md bg-zinc-950 dark:bg-zinc-100 hover:opacity-90 active:scale-[0.99] text-white dark:text-zinc-950 text-xs sm:text-sm font-semibold flex items-center justify-center transition-all shadow-xs cursor-pointer"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Sign In"
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* 2-Column Split: Form on Left, "or" in Center, Google on Right */
            <div className="flex flex-col sm:flex-row items-stretch gap-6 sm:gap-8">
              {/* Left Column: Email & Password form */}
              <div className="flex-1 min-w-0">
                <form onSubmit={handleEmailSubmit} className="space-y-3.5">
                  <div className="space-y-1.5 text-left">
                    <label
                      htmlFor="client-email-input"
                      className="text-xs font-semibold text-foreground"
                    >
                      Email
                    </label>
                    <input
                      id="client-email-input"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder="Type your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-white transition-all shadow-xs"
                    />
                  </div>

                  <div className="space-y-1.5 text-left">
                    <label
                      htmlFor="client-password-input"
                      className="text-xs font-semibold text-foreground"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="client-password-input"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 pl-3 pr-9 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-white transition-all shadow-xs"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      backgroundColor: "var(--color-brand-primary)",
                      color: "var(--color-brand-foreground)",
                    }}
                    className="w-full h-10 rounded-md bg-zinc-950 dark:bg-zinc-100 hover:opacity-90 active:scale-[0.99] text-white dark:text-zinc-950 text-xs sm:text-sm font-semibold flex items-center justify-center transition-all shadow-xs mt-3 cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>
              </div>

              {/* Center Divider: Vertical on sm+, horizontal on mobile */}
              <div className="relative flex items-center justify-center py-2 sm:py-0 sm:px-2">
                <div className="hidden sm:block absolute inset-y-2 left-1/2 -translate-x-1/2 w-[1px] bg-zinc-200 dark:bg-zinc-800" />
                <div className="sm:hidden absolute inset-x-0 top-1/2 -translate-y-1/2 h-[1px] bg-zinc-200 dark:bg-zinc-800" />
                <span className="relative z-10 bg-background dark:bg-zinc-950 px-3 py-1 text-xs text-muted-foreground select-none uppercase tracking-wider font-mono">
                  or
                </span>
              </div>

              {/* Right Column: Google Sign-in */}
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <button
                  type="button"
                  onClick={handleGoogleClick}
                  className="w-full h-11 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800/80 text-foreground text-xs sm:text-sm font-medium flex items-center justify-center gap-2.5 transition-colors shadow-xs"
                >
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                  <span>Continue with Google</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Legal Terms Footer */}
      <footer className="w-full pb-2 text-center">
        <p className="text-[11px] text-muted-foreground max-w-sm mx-auto leading-relaxed">
          By clicking &ldquo;Sign in with Google&rdquo; or &ldquo;Continue with email&rdquo;
          <br className="hidden sm:inline" /> you agree to our{" "}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Privacy policy
          </Link>
          .
        </p>
      </footer>
    </main>
  );
}
